import { stringifyQueryParams } from "providers/queryParams";
import { useCallback } from "react";

import { useQueryParams } from "../../hooks/useQueryParams";

export type Filter = {
  omnisearch: string;
  updateOmnisearch: (filters: [string, string][], clearFirst?: boolean) => void;
  removeOmnisearch: (filter: string) => void;
  getFilter: (filter: string) => string | undefined;
  getOmnisearchUrl: (baseUrl: string, filters: [string, string][]) => string;
};

export default function useOmnisearchFilters(): Filter {
  const { filters, setFilters } = useQueryParams<Filter>();

  const updateOmnisearch = useCallback(
    (filters: [string, string][], clearFirst?: boolean) => {
      setFilters((state) => {
        state.omnisearch = getOmnisearchValueFromFilters(
          filters,
          !clearFirst ? (state.omnisearch ?? "") : ""
        );
      });
    },
    [setFilters]
  );

  const getOmnisearchUrl = useCallback((baseUrl: string, filters: [string, string][]) => {
    const omnisearch = getOmnisearchValueFromFilters(filters);

    return `${baseUrl}?${stringifyQueryParams({
      omnisearch,
    })}`;
  }, []);

  const removeOmnisearch = useCallback(
    (filter: string) => {
      setFilters((state) => {
        state.omnisearch = removeKeyInOmnisearch(state.omnisearch ?? "", filter);
      });
    },
    [setFilters]
  );

  const getFilter = useCallback(
    (filter: string) => {
      const { kvpValues } = breakApartOmnisearch(filters.omnisearch ?? "");
      const kvp = kvpValues.find(([k]) => k === filter);
      return kvp?.[1];
    },
    [filters]
  );

  return {
    omnisearch: filters.omnisearch ?? "",
    updateOmnisearch,
    removeOmnisearch,
    getFilter,
    getOmnisearchUrl,
  };
}

function getOmnisearchValueFromFilters(filters: [string, string][], initialOmnisearch = "") {
  let omnisearch = initialOmnisearch;
  for (const [key, value] of filters) {
    omnisearch = addOrUpdateKeyInOmnisearch(omnisearch, key, value);
  }
  return omnisearch;
}

function addOrUpdateKeyInOmnisearch(omnisearch: string, key: string, value: string) {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(omnisearch);

  const filteredKvpValues = kvpValues.filter(([k]) => k !== key); // remove the old value if it exists, we're updating

  filteredKvpValues.push([key, value]); // add the new value
  return `${nonKvpValues} ${filteredKvpValues.map(([k, v]) => `${k}:${v}`).join(" ")}`.trim();
}

function removeKeyInOmnisearch(omnisearch: string, key: string) {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(omnisearch);
  const filteredKvpValues = kvpValues.filter(([k]) => k !== key); // remove the old value if it exists
  return `${nonKvpValues} ${filteredKvpValues.map(([k, v]) => `${k}:${v}`).join(" ")}`.trim();
}

function breakApartOmnisearch(omnisearch: string) {
  const tokens = parseOmnisearchTokens(omnisearch);
  const nonKvpValues: string[] = [];
  const kvpValues: [string, string][] = [];

  for (const token of tokens) {
    const colonIndex = token.indexOf(":");
    if (colonIndex > 0) {
      // This is a key:value pair
      const key = token.substring(0, colonIndex);
      const value = token.substring(colonIndex + 1);
      kvpValues.push([key, value]);
    } else {
      // This is a non-key-value token
      nonKvpValues.push(token);
    }
  }

  return {
    nonKvpValues: nonKvpValues.join(" "),
    kvpValues,
  };
}

function parseOmnisearchTokens(search: string): string[] {
  const tokens: string[] = [];
  let current = "";
  let inQuotes = false;
  let quoteChar = "";

  for (let i = 0; i < search.length; i++) {
    const char = search[i];

    if (!inQuotes && (char === '"' || char === "'")) {
      // Starting a quoted section
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      // Ending the quoted section
      inQuotes = false;
      quoteChar = "";
      current += char;
    } else if (!inQuotes && char === " ") {
      // Space outside quotes - end current token
      if (current.trim()) {
        tokens.push(current.trim());
        current = "";
      }
    } else {
      // Regular character
      current += char;
    }
  }

  // Add the last token if there is one
  if (current.trim()) {
    tokens.push(current.trim());
  }

  return tokens;
}
